import { TPSScores } from '../types/tps.types';
import {
  signedDifferentialScore,
  softmaxRankings,
  inferredConfidence,
  userTraitMean,
  type DifferentialTraitSet,
} from './utils';

export interface TrueColorDetail {
  primary: string;
  secondary: string;
  /** Per-color softmax percentages summing to ~100. */
  colorScores: Record<string, number>;
  confidence: number;
  confidenceLabel: 'Low' | 'Medium' | 'High';
  mixed: boolean;
  noSignal: boolean;
  description: string;
  strengths: string[];
  challenges: string[];
}

export const TRUE_COLOR_TRAITS: Record<
  string,
  DifferentialTraitSet & {
    keirsey: string;
    description: string;
    strengths: string[];
    challenges: string[];
  }
> = {
  Gold: {
    // Guardian/SJ
    positive: ['Structured', 'Lawful', 'Self-Mastery', 'Pragmatic', 'Realistic'],
    positiveModerate: ['Static', 'Stoic', 'Communal Navigate'],
    negative: ['Dynamic', 'Varied', 'Self-Indulgent'],
    signatureTrait: 'Structured',
    keirsey: 'Guardian',
    description: 'Organized, responsible, and traditional',
    strengths: ['Planning', 'Reliability', 'Punctuality', 'Organization'],
    challenges: ['Flexibility', 'Spontaneity', 'Change adaptation'],
  },
  Orange: {
    // Artisan/SP
    positive: ['Dynamic', 'Physical', 'Direct', 'Varied', 'Pragmatic'],
    positiveModerate: ['Optimistic', 'Assertive', 'Extrinsic'],
    negative: ['Structured', 'Static', 'Analytical'],
    signatureTrait: 'Dynamic',
    keirsey: 'Artisan',
    description: 'Spontaneous, energetic, and adaptable',
    strengths: ['Adaptability', 'Problem-solving', 'Energy', 'Fun'],
    challenges: ['Long-term planning', 'Routine', 'Patience'],
  },
  Blue: {
    // Idealist/NF
    positive: ['Intuitive', 'Responsive', 'Communal Navigate', 'Diplomatic', 'Self-Aware'],
    positiveModerate: ['Social', 'Universal', 'Intrinsic', 'Self-Aware'], // TPS-EXEMPT: Social=processing.reality.social
    negative: ['Stoic', 'Independent Navigate', 'Pragmatic'],
    signatureTrait: 'Diplomatic',
    keirsey: 'Idealist',
    description: 'Empathetic, harmonious, and idealistic',
    strengths: ['Empathy', 'Communication', 'Inspiration', 'Harmony'],
    challenges: ['Criticism', 'Conflict', 'Practicality'],
  },
  Green: {
    // Rational/NT
    positive: ['Analytical', 'Independent', 'Self-Principled', 'Intuitive', 'Universal'], // TPS-EXEMPT: Independent=interpersonal.navigate.independent
    positiveModerate: ['Independent Navigate', 'Stoic', 'Self-Mastery'],
    negative: ['Social', 'Responsive', 'Physical'], // TPS-EXEMPT: Social=processing.reality.social
    signatureTrait: 'Analytical',
    keirsey: 'Rational',
    description: 'Analytical, inventive, and curious',
    strengths: ['Analysis', 'Innovation', 'Logic', 'Problem-solving'],
    challenges: ['Emotions', 'Small talk', 'Repetition'],
  },
};

export function calculateTrueColor(scores: TPSScores): TrueColorDetail {
  const mean = userTraitMean(scores);

  const raw = Object.entries(TRUE_COLOR_TRAITS).map(([color, traits]) => ({
    key: color,
    score: signedDifferentialScore(scores, traits, mean),
    tiebreak: traits.signatureTrait ? (scores[traits.signatureTrait] ?? mean) - mean : 0,
  }));

  const ranked = softmaxRankings(raw);
  const { confidence, confidenceLabel, mixed, noSignal } = inferredConfidence(ranked);

  const primary = ranked[0].key;
  const secondary = ranked[1]?.key ?? primary;
  const primaryConfig = TRUE_COLOR_TRAITS[primary];

  const colorScores: Record<string, number> = {};
  for (const r of ranked) colorScores[r.key] = r.percentage;

  return {
    primary,
    secondary,
    colorScores,
    confidence,
    confidenceLabel,
    mixed,
    noSignal,
    description: primaryConfig.description,
    strengths: primaryConfig.strengths,
    challenges: primaryConfig.challenges,
  };
}

export const TRUE_COLOR_HEX: Record<string, string> = {
  Gold: 'hsl(45, 90%, 50%)',
  Orange: 'hsl(30, 90%, 55%)',
  Blue: 'hsl(210, 80%, 50%)',
  Green: 'hsl(140, 60%, 40%)',
};
